import React, { useEffect, useRef, useState } from "react";

export const Eyebrow = ({ children, tone = "dark", className = "" }) => (
  <div
    className={`eyebrow ${className}`}
    style={{ color: tone === "light" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.55)" }}
  >
    {children}
  </div>
);

export const SectionHead = ({ eyebrow, children, tone = "dark", align = "left", max = "48rem" }) => (
  <div style={{ textAlign: align, maxWidth: max, margin: align === "center" ? "0 auto" : undefined }}>
    {eyebrow && <Eyebrow tone={tone} className="mb-5">{eyebrow}</Eyebrow>}
    <h2
      className="font-display"
      style={{
        color: tone === "light" ? "#fff" : "var(--hv-ink)",
        fontSize: "clamp(2.4rem, 5.6vw, 5rem)",
      }}
    >
      {children}
    </h2>
  </div>
);

// Fire-and-forget reveal (stays visible after first entry)
export const Reveal = ({ children, delay = 0, as: Tag = "div", className = "", ...rest }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }),
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Tag
      ref={ref}
      className={`hv-reveal ${inView ? "is-in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export const Counter = ({ to, duration = 1800, format = (n) => n.toLocaleString() }) => {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        const start = performance.now();
        const tick = (t) => {
          const p = Math.min(1, (t - start) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          setN(Math.round(eased * to));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        io.disconnect();
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [to, duration]);
  return <span ref={ref} className="ticker">{format(n)}</span>;
};

export const Chip = ({ children, tone = "dark" }) => (
  <span
    className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.2em] uppercase px-3 py-1.5 rounded-full"
    style={{
      border: `1px solid ${tone === "light" ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.15)"}`,
      color: tone === "light" ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.7)",
    }}
  >
    {children}
  </span>
);

export const Marquee = ({ items, tone = "dark" }) => (
  <div className="overflow-hidden py-6 relative" style={{ maskImage: "linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent)" }}>
    <div className="hv-marquee-track">
      {[...items, ...items].map((t, i) => (
        <span
          key={i}
          className="text-[0.8rem] tracking-[0.3em] uppercase"
          style={{ color: tone === "light" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)" }}
        >
          {t}
          <span className="ml-12 opacity-40">◇</span>
        </span>
      ))}
    </div>
  </div>
);
